/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {TemplatePortal} from '@angular/material/core';
import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import {CanDisable, mixinDisabled} from '@angular/material/core';
import {MdTabLabel} from './tab-label';
import {Subject} from 'rxjs/Subject';
/**
 * \@docs-private
 */
export class MdTabBase {}
export const /** @type {?} */ _MdTabMixinBase = mixinDisabled(MdTabBase);
export class MdTab extends _MdTabMixinBase implements OnInit, CanDisable, OnChanges, OnDestroy {
/**
 * Content for the tab label given by <ng-template md-tab-label>.
 */
templateLabel: MdTabLabel;
/**
 * Template inside the MdTab view that contains an <ng-content>.
 */
_content: TemplateRef<any>;
/**
 * The plain text label for the tab, used when there is no template label.
 */
textLabel: string = '';
/**
 * The portal that will be the hosted content of the tab
 */
private _contentPortal: TemplatePortal<any> | null = null;
/**
 * @return {?}
 */
get content(): TemplatePortal<any> | null { return this._contentPortal; }
/**
 * Emits whenever the label changes.
 */
_labelChange = new Subject<void>();
/**
 * Emits whenevfer the disable changes
 */
_disableChange = new Subject<void>();
/**
 * The relatively indexed position where 0 represents the center, negative is left, and positive
 * represents the right.
 */
position: number | null = null;
/**
 * The initial relatively index origin of the tab if it was created and selected after there
 * was already a selected tab. Provides context of what position the tab should originate from.
 */
origin: number | null = null;
/**
 * Whether the tab is currently active.
 */
isActive = false;
/**
 * @param {?} _viewContainerRef
 */
constructor(private _viewContainerRef: ViewContainerRef) {
    super();
  }
/**
 * @param {?} changes
 * @return {?}
 */
ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty('textLabel')) {
      this._labelChange.next();
    }

    if (changes.hasOwnProperty('disabled')) {
      this._disableChange.next();
    }
  }
/**
 * @return {?}
 */
ngOnDestroy(): void {
    this._disableChange.complete();
    this._labelChange.complete();
  }
/**
 * @return {?}
 */
ngOnInit(): void {
    this._contentPortal = new TemplatePortal(this._content, this._viewContainerRef);
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  moduleId: module.id,
  selector: 'md-tab, mat-tab',
  templateUrl: 'tab.html',
  inputs: ['disabled'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  preserveWhitespaces: false,
  exportAs: 'mdTab, matTab',
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ViewContainerRef, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'templateLabel': [{ type: ContentChild, args: [MdTabLabel, ] },],
'_content': [{ type: ViewChild, args: [TemplateRef, ] },],
'textLabel': [{ type: Input, args: ['label', ] },],
};
}

function MdTab_tsickle_Closure_declarations() {
/** @type {?} */
MdTab.decorators;
/**
 * @nocollapse
 * @type {?}
 */
MdTab.ctorParameters;
/** @type {?} */
MdTab.propDecorators;
/**
 * Content for the tab label given by <ng-template md-tab-label>.
 * @type {?}
 */
MdTab.prototype.templateLabel;
/**
 * Template inside the MdTab view that contains an <ng-content>.
 * @type {?}
 */
MdTab.prototype._content;
/**
 * The plain text label for the tab, used when there is no template label.
 * @type {?}
 */
MdTab.prototype.textLabel;
/**
 * The portal that will be the hosted content of the tab
 * @type {?}
 */
MdTab.prototype._contentPortal;
/**
 * Emits whenever the label changes.
 * @type {?}
 */
MdTab.prototype._labelChange;
/**
 * Emits whenevfer the disable changes
 * @type {?}
 */
MdTab.prototype._disableChange;
/**
 * The relatively indexed position where 0 represents the center, negative is left, and positive
 * represents the right.
 * @type {?}
 */
MdTab.prototype.position;
/**
 * The initial relatively index origin of the tab if it was created and selected after there
 * was already a selected tab. Provides context of what position the tab should originate from.
 * @type {?}
 */
MdTab.prototype.origin;
/**
 * Whether the tab is currently active.
 * @type {?}
 */
MdTab.prototype.isActive;
/** @type {?} */
MdTab.prototype._viewContainerRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
